import {BioentityInfo} from 'folder-items/FileConstants';
import getValuesInSelection from 'notebook/structured-tables/util/getValuesInSelection';
import {RowData} from 'notebook/tables/constants/RowConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {
  getBioentityInfosFromTypedValue,
  getStorableInfosFromTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {StorableInfo} from 'notebook/tables/typed-values/TypedValueTypes';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

import {StructuredTableColumn, StructuredTableKind} from '../StructuredTableConstants';

function getStorableIds(typedValue: TypedValue): Array<string> {
  let storableInfos: Array<StorableInfo>;
  switch (typedValue.type) {
    case BaseTypedValueType.STORABLES:
      storableInfos = getStorableInfosFromTypedValue(typedValue);
      break;
    case FieldType.STORABLE_LINK:
      storableInfos = typedValue.targetStorables;
      break;
    default:
      storableInfos = [];
      break;
  }
  return storableInfos.map(({id}) => id);
}

function getBioentityIds(typedValue: TypedValue): Array<string> {
  let bioentitySummaries: Array<BioentityInfo>;
  switch (typedValue.type) {
    case BaseTypedValueType.BIOENTITY:
      bioentitySummaries = getBioentityInfosFromTypedValue(typedValue) ?? [];
      break;
    case FieldType.BIOENTITY_LINK:
      bioentitySummaries = typedValue.targetFiles.filter(({id}) => id != null) as Array<BioentityInfo>;
      break;
    default:
      bioentitySummaries = [];
      break;
  }
  return bioentitySummaries.map(({id}) => id);
}

const STRUCTURED_TABLE_KIND_INCLUDE_SCHEMA_FIELD_COLUMNS = new Set([
  StructuredTableKind.PROCESS_OUTPUT,
  StructuredTableKind.PROCESS_TASK,
]);

export const SELECT_ITEMS_TO_PRINT_TOOLTIP_TEXT = 'Select item(s) with registry IDs or barcodes to print';

export default function getBarcodePrintableIdsInSelection(
  multiSelectionBox: MultiSelectionBox | null,
  columns: Array<StructuredTableColumn>,
  computedTable: Array<Array<EvaluatedCell>>,
  table: Array<RowData>,
  kind?: StructuredTableKind
): {printableIds: Array<string>; tooltip: string | null} {
  // TODO(BNCH-44733): This is a hack to only include entities in schema field columns for a specific set of
  // column types. This should be removed soon in favor of a refactor which handles printable barcodes in a
  // way that is consistent table wide, but we implmented this hack to make it easier for users working with
  // registration tables, plate tables, etc to print barcodes.
  const includeTagValueCol = kind != null && STRUCTURED_TABLE_KIND_INCLUDE_SCHEMA_FIELD_COLUMNS.has(kind);

  const bioentityIds = getValuesInSelection(multiSelectionBox, columns, computedTable, table || [], {
    columnFilter: (column) =>
      [
        TableColumnType.EXISTING_BIOENTITY,
        ...(includeTagValueCol ? [TableColumnType.TAG_VALUE] : []),
      ].includes(column.columnType),
    cellFilter: (cellValue, _, rowData) =>
      cellValue.type === BaseTypedValueType.BIOENTITY || cellValue.type === FieldType.BIOENTITY_LINK,
    getCellValues: (cellValue) => getBioentityIds(cellValue),
  });

  const storableIds = getValuesInSelection(multiSelectionBox, columns, computedTable, table || [], {
    columnFilter: (column) =>
      [
        TableColumnType.CONTAINERS,
        TableColumnType.PLATE,
        TableColumnType.BOX,
        TableColumnType.PARENT_STORABLE,
        ...(includeTagValueCol ? [TableColumnType.TAG_VALUE] : []),
      ].includes(column.columnType),
    cellFilter: (cellValue) =>
      cellValue.type === BaseTypedValueType.STORABLES || cellValue.type === FieldType.STORABLE_LINK,
    getCellValues: (cellValue) => getStorableIds(cellValue),
  });

  // If both types are selected, we should prevent printing.
  if (bioentityIds.length > 0 && storableIds.length > 0) {
    return {printableIds: [], tooltip: 'Select only bioentities or only inventory items to print'};
  }

  const printableIds = bioentityIds.concat(storableIds);
  return {
    printableIds,
    tooltip: !printableIds.length ? SELECT_ITEMS_TO_PRINT_TOOLTIP_TEXT : null,
  };
}
